import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTransactionMemo,
  deleteTransactionMemo,
  getTransactionMemos,
  TransactionIdentifier,
  updateTransactionMemo,
} from '~apis/cash-flow-overview/transactions/memos';
import { TransactionsQueryKey } from '~hooks/queries/transactions/query';

export const TransactionsMemosQuery = {
  default: () => ['transactionsMemo'],
  memos: ({ companyId, transactionId }: TransactionIdentifier) => [
    ...TransactionsMemosQuery.default(),
    'memos',
    companyId,
    transactionId,
  ],
};

export function useTransactionMemos(identifier: TransactionIdentifier, trigger?: boolean) {
  return useQuery({
    queryKey: TransactionsMemosQuery.memos(identifier),
    queryFn: () => getTransactionMemos(identifier),
    enabled: identifier.companyId != null && (trigger ?? true),
    refetchOnMount: true,
  });
}

export function useCreateTransactionMemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTransactionMemo,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(TransactionsMemosQuery.default());
      queryClient.invalidateQueries(TransactionsQueryKey.default());
    },
  });
}

export function useUpdateTransactionMemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTransactionMemo,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(TransactionsMemosQuery.default());
      queryClient.invalidateQueries(TransactionsQueryKey.default());
    },
  });
}

export function useDeleteTransactionMemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTransactionMemo,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(TransactionsMemosQuery.memos(variables));
      queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: variables.companyId }));
    },
  });
}
